import dayjs from 'dayjs';

import { ICreatePromoCodeRequest, PromoCodeRequestInput } from 'schemas';
import { EPromoScopeType, EPromoValueType } from 'types';

import {
  ETabsModal,
  IChosenClientItem,
  ISubmitPromoParams,
} from './interfaces';

export const buildApplicableParams = (
  chosenPositions: Array<{
    id: number;
    title: string;
    type?: ETabsModal;
  }>
): ICreatePromoCodeRequest['input']['applicable_params'] => {
  if (!chosenPositions.length) return undefined;

  const result: ICreatePromoCodeRequest['input']['applicable_params'] = {};

  const positionsByType: Record<
    string,
    Array<{ id: number; title: string }>
  > = {};

  chosenPositions.forEach((p) => {
    if (!p.type) return;

    const typeKey = p.type.toLowerCase();
    if (!positionsByType[typeKey]) {
      positionsByType[typeKey] = [];
    }
    positionsByType[typeKey].push({ id: p.id, title: p.title });
  });

  Object.entries(positionsByType).forEach(([type, positions]) => {
    const typeKey = type as 'goods' | 'services' | 'tariffs';

    const hasAllPosition = positions.some((p) => p.id === -1);

    if (hasAllPosition) {
      result[typeKey] = [-1];
    } else {
      result[typeKey] = positions.map((p) => p.id);
    }
  });

  return Object.keys(result).length > 0 ? result : undefined;
};

export const buildClientScopeParams = (
  chosenItems: Array<IChosenClientItem>
): ICreatePromoCodeRequest['input']['client_scope'] => {
  if (!chosenItems.length) return undefined;

  const result: { groups: Array<string>; users: Array<string> } = {
    groups: [],
    users: [],
  };

  chosenItems.forEach(({ type, id }) => {
    if (type === 'client') result.users.push(id);
    if (type === 'group') result.groups.push(id);
  });

  return result;
};

export const normaliseSubmitPromoInput = ({
  values,
  chosenItems,
  chosenPositions,
  areas,
  rewardType,
}: ISubmitPromoParams): PromoCodeRequestInput => {
  const input: ICreatePromoCodeRequest['input'] = {
    code: values.code,
    value: {
      type: values.rewardType as EPromoValueType,
      value: Number(values.rewardValue),
    },
    active: values.active,
    is_tracked: values.is_tracked,
  };

  const applicable_params =
    rewardType === EPromoValueType.DISCOUNT
      ? buildApplicableParams(chosenPositions)
      : null;

  const scopes: Array<EPromoScopeType> | undefined =
    areas
      .filter((area) => area.active)
      .map((area) => area.value as EPromoScopeType) || undefined;

  const client_scope = buildClientScopeParams(chosenItems);

  if (applicable_params) {
    input.applicable_params = applicable_params;
  } else {
    input.applicable_params = null;
  }

  if (scopes?.length) {
    input.scopes = scopes;
  }

  if (values.amount) {
    input.amount = values.amount;
  }

  if (client_scope) {
    input.client_scope = client_scope;
  } else {
    input.client_scope = null;
  }

  if (values.activeFrom) {
    input.active_from = dayjs(values.activeFrom).format('YYYY-MM-DD HH:mm:ss');
  } else {
    input.active_from = null;
  }

  if (values.activeTo) {
    input.active_to = dayjs(values.activeTo).format('YYYY-MM-DD HH:mm:ss');
  } else {
    input.active_to = null;
  }

  return input;
};
